import { Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { Button } from '../../../components/ui/button'
import { Badge } from '../../../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog'
import { ArrowLeftIcon, CopyIcon, CheckIcon, BanIcon } from 'lucide-react'

interface AccessCode {
  id: number
  code: string
  name: string
  plan_tier: string
  duration_hours: number
  max_redemptions: number
  redemptions_count: number
  remaining_uses: number
  bonus_credits: number
  expires_at: string | null
  deactivated_at: string | null
  status: string
  created_by_email: string
  created_at: string
}

interface Redemption {
  id: number
  organization_name: string
  organization_id: number
  redeemed_by_email: string
  previous_plan: string
  granted_plan: string
  bonus_credits_granted: number
  expires_at: string
  expired_at: string | null
  reverted_at: string | null
  active: boolean
  created_at: string
}

interface Props {
  access_code: AccessCode
  redemptions: Redemption[]
  auth: { user: { id: number; name: string; email: string; admin: boolean; super_admin?: boolean } }
}

function statusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'active': return 'default'
    case 'deactivated': return 'destructive'
    case 'expired': return 'secondary'
    case 'fully_redeemed': return 'outline'
    default: return 'secondary'
  }
}

function formatDuration(hours: number): string {
  if (hours < 24) return `${hours} hours`
  if (hours < 168) return `${Math.round(hours / 24)} days`
  return `${Math.round(hours / 168)} weeks`
}

function Show({ access_code, redemptions }: Props) {
  const { t } = useTranslation('admin')
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleCopyCode = () => {
    navigator.clipboard.writeText(access_code.code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDeactivate = () => {
    router.post(`/admin/access_codes/${access_code.id}/deactivate`, {}, {
      onSuccess: () => setShowDeactivateDialog(false)
    })
  }

  return (
    <>
      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-2 py-2">
            <div className="px-4 lg:px-6">
              {/* Back link */}
              <Link href="/admin/access_codes" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeftIcon className="h-4 w-4" />
                {t('accessCodes.show.backToList')}
              </Link>

              {/* Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{access_code.name}</h1>
                  <div className="mt-2 flex items-center gap-3">
                    <code className="rounded bg-muted px-2 py-1 font-mono text-lg">{access_code.code}</code>
                    <Button variant="ghost" size="sm" onClick={handleCopyCode}>
                      {copied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
                    </Button>
                    <Badge variant={statusBadgeVariant(access_code.status)}>
                      {t(`accessCodes.badges.${access_code.status === 'fully_redeemed' ? 'fullyRedeemed' : access_code.status}`)}
                    </Badge>
                  </div>
                </div>
                {access_code.status === 'active' && (
                  <Button variant="destructive" onClick={() => setShowDeactivateDialog(true)}>
                    <BanIcon className="mr-2 h-4 w-4" />
                    {t('accessCodes.show.deactivate')}
                  </Button>
                )}
              </div>

              {/* Details Card */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>{t('accessCodes.show.details')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    <div>
                      <dt className="text-sm text-muted-foreground">{t('accessCodes.show.plan')}</dt>
                      <dd className="mt-1 font-medium capitalize">{access_code.plan_tier}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">{t('accessCodes.show.duration')}</dt>
                      <dd className="mt-1 font-medium">{formatDuration(access_code.duration_hours)}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">{t('accessCodes.show.maxRedemptions')}</dt>
                      <dd className="mt-1 font-medium">{access_code.redemptions_count} / {access_code.max_redemptions}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">{t('accessCodes.show.bonusCredits')}</dt>
                      <dd className="mt-1 font-medium">{access_code.bonus_credits}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">{t('accessCodes.show.codeExpires')}</dt>
                      <dd className="mt-1 font-medium">
                        {access_code.expires_at ? new Date(access_code.expires_at).toLocaleString() : t('accessCodes.show.never')}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">{t('accessCodes.show.createdBy')}</dt>
                      <dd className="mt-1 font-medium">{access_code.created_by_email}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">{t('accessCodes.show.createdAt')}</dt>
                      <dd className="mt-1 font-medium">{new Date(access_code.created_at).toLocaleString()}</dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>

              {/* Redemptions Table */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('accessCodes.show.redemptions')} ({redemptions.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.show.redemptionsTable.organization')}</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.show.redemptionsTable.redeemedBy')}</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.show.redemptionsTable.grantedPlan')}</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.show.redemptionsTable.expiresAt')}</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.show.redemptionsTable.status')}</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('accessCodes.show.redemptionsTable.redeemedAt')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {redemptions.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                              {t('accessCodes.show.noRedemptions')}
                            </td>
                          </tr>
                        ) : (
                          redemptions.map((r) => (
                            <tr key={r.id} className="border-b hover:bg-muted/30 transition-colors">
                              <td className="px-4 py-3">
                                <Link href={`/admin/organizations/${r.organization_id}`} className="text-sm font-medium text-primary hover:underline">
                                  {r.organization_name}
                                </Link>
                              </td>
                              <td className="px-4 py-3 text-sm">{r.redeemed_by_email}</td>
                              <td className="px-4 py-3">
                                <Badge variant="outline" className="capitalize">{r.granted_plan}</Badge>
                              </td>
                              <td className="px-4 py-3 text-sm">{new Date(r.expires_at).toLocaleString()}</td>
                              <td className="px-4 py-3">
                                {r.active ? (
                                  <Badge variant="default">Active</Badge>
                                ) : (
                                  <Badge variant="secondary">Expired</Badge>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">
                                {new Date(r.created_at).toLocaleString()}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Deactivate Dialog */}
      <AlertDialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('accessCodes.show.deactivate')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('accessCodes.show.deactivateConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('accessCodes.show.deactivate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
